/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './Code';

const CodeManager: ComponentManagerFn<'Code'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Code'>(platform, 'Code'),
    type: 'Code',
    name: 'Code',
    isChildAllowed: (page, item, childType) =>
      ([] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      viewAttribute: {
        label: 'View Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String', 'Textarea', 'JSON'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      insertable: {
        label: 'Insertable',
        type: 'booleanExpression',
        category: 'Data',
      },
      updatable: {
        label: 'Updatable',
        type: 'booleanExpression',
        category: 'Data',
      },
      language: {
        label: 'Language',
        type: 'string',
        helpText:
          'A supported language by the Monaco Editor e.g. typescript, xml, java, plsql etc.',
      },
    },
    getDisplayText: (item) => item.props.viewAttribute ?? '',
    category: 'Forms',
    icon: { iconName: 'code' },
    description: `**Overview**<br/>
The Code component is a high-performance text editor optimized for writing software code. It is powered by the Monaco Editor, the same engine that drives Visual Studio Code.
Use this component when you need users to edit JSON configurations, SQL queries, HTML templates, or Script logic directly within your application. It provides standard IDE features like syntax highlighting, line numbering, and automatic indentation.

**1. Data Binding**<br/>
This component binds to a text field in your database to store the raw code string.
* **View Attribute:** Select the Data Store field.
* **Supported Types:** String, Textarea, JSON.
* **Note:** If binding to a JSON column, the editor automatically validates the JSON syntax.

**2. Syntax Highlighting**<br/>
You can configure the editor to understand the specific language being written, enabling color-coded syntax and basic error checking.
* **Language:** A string specifying the programming language.
* **Common Values:** \`typescript\`, \`javascript\`, \`json\`, \`xml\`, \`html\`, \`css\`, \`java\`, \`python\`, \`sql\`, \`plsql\`, \`markdown\`.
* **Behavior:** If set to \`json\`, the editor will highlight keys and values differently. If \`sql\`, it will highlight keywords like \`SELECT\` or \`FROM\`.

**3. Permissions & State**<br/>
Like standard form fields, you can control whether the code is editable.
* **Insertable / Updatable:** Boolean expressions (e.g., \`$.user.role === 'Developer'\`).
* **Enabled:** The user can type and modify the code.
* **Disabled:** The editor renders in **Read-Only** mode. This is perfect for displaying logs, audit trails, or configuration snippets that users should see but not touch.

**4. Best Use Cases**<br/>
* **Admin Panels:** Allowing admins to edit raw JSON configuration blobs.
* **Developer Tools:** Building an internal tool to run SQL queries or preview XML payloads.
* **CMS:** Editing HTML or Markdown templates for email campaigns.`,
  };
};

export default CodeManager;
